import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft, Mail, CheckCircle2, Gift } from 'lucide-react';
import axios from 'axios';
import { BRAND_IMAGES } from '@/constants/brandImages';

const API_URL = window.location.origin;

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [giftActivated, setGiftActivated] = useState(false);
  
  // Forgot Password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);

  // Check for gift activation success
  useEffect(() => {
    if (searchParams.get('gift_activated') === 'true') {
      setGiftActivated(true);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const userData = await login(email, password);
      toast.success('Welcome back!');
      
      // Redirect coaches to coach dashboard, others to regular dashboard
      if (userData?.is_coach && userData?.role !== 'admin') {
        navigate('/coach');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email: forgotEmail });
      setForgotSuccess(true);
    } catch (error) {
      // Always show success message for security (don't reveal if email exists)
      setForgotSuccess(true);
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-forest hover:text-terracotta mb-8 transition-colors">
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>

          <Card className="card-base border-none shadow-lg">
            <CardHeader className="space-y-1">
              <CardTitle className="font-heading text-3xl text-forest">Welcome, Sis</CardTitle>
              <CardDescription>
                Your sanctuary is waiting. Step back into your space.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Gift Activation Success Banner */}
              {giftActivated && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Gift className="h-5 w-5 text-green-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-green-800">Gift Membership Activated!</p>
                      <p className="text-sm text-green-700 mt-1">
                        Check your email for your login credentials. Use them to sign in below.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="input-base"
                    data-testid="login-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="input-base pr-10"
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-forest"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => { setShowForgotPassword(true); setForgotEmail(email); setForgotSuccess(false); }}
                    className="text-sm text-forest hover:text-terracotta transition-colors"
                  >
                    Forgot password?
                  </button>
                </div>

                <Button 
                  type="submit" 
                  className="w-full btn-primary h-12" 
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-gray-600">
                  Don't have an account?{' '}
                  <Link to="/inner-circle" className="text-forest font-medium hover:text-terracotta">
                    Join Inner Circle
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Right Side - Branding with Image */}
      <div className="hidden lg:flex flex-1 hero-gradient items-center justify-center p-12 relative overflow-hidden">
        {/* Background Image Overlay */}
        <div className="absolute inset-0 opacity-15">
          <img 
            src={BRAND_IMAGES.sisterhood.connection} 
            alt="" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-lg text-white relative z-10">
          <h1 className="font-heading text-5xl font-bold mb-6">
            Return to Your Sanctuary
          </h1>
          <p className="text-xl text-white/80 mb-8 max-w-lg leading-relaxed">
            You've been carrying the world, Sis. This is your space to exhale, 
            reconnect with your sisters, and remember who you are beyond the hustle.
          </p>
          <div className="flex items-center gap-4">
            <div className="flex -space-x-3">
              {['A', 'B', 'C', 'D'].map((letter, i) => (
                <div key={i} className="h-10 w-10 rounded-full bg-white/20 border-2 border-white flex items-center justify-center font-semibold">
                  {letter}
                </div>
              ))}
            </div>
            <p className="text-white/80">
              500+ brilliant women holding space for each other
            </p>
          </div>
        </div>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-forest">
              {forgotSuccess ? 'Check Your Email' : 'Reset Your Password'}
            </DialogTitle>
            <DialogDescription>
              {forgotSuccess 
                ? 'If an account exists with this email, you will receive password reset instructions shortly.'
                : 'Enter your email address and we\'ll send you instructions to reset your password.'
              }
            </DialogDescription>
          </DialogHeader>
          
          {forgotSuccess ? (
            <div className="py-6 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
              <p className="text-gray-600 mb-4">
                Please check your inbox at <strong>{forgotEmail}</strong>
              </p>
              <Button onClick={() => setShowForgotPassword(false)} className="btn-primary">
                Back to Login
              </Button>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="forgot-email">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="forgot-email"
                    type="email"
                    placeholder="you@example.com"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                    className="input-base pl-10"
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>
              
              <div className="flex gap-3">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowForgotPassword(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1 btn-primary" 
                  disabled={forgotLoading}
                >
                  {forgotLoading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoginPage;
